import { useState, useMemo } from "react";
import { Card, Progress, Row, Col, Tag, Table, Button, Tooltip, message } from "antd";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ChartTooltip,
    Legend,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie
} from "recharts";
import type { ColumnsType } from "antd/es/table";
import type { DashboardMetrics } from "../types";
import { DownloadOutlined, InfoCircleOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";

interface PipelineStatusProps {
    data: DashboardMetrics;
}

interface PipelineItem {
    key: string;
    stage: string;
    count: number;
    target: number;
    conversion: number;
    avgDays: number;
    status: 'on-track' | 'at-risk' | 'delayed';
    description: string;
}

export const PipelineStatus = ({ data }: PipelineStatusProps) => {
    const pipeline = data?.recruitment_process?.pipeline_status || {};

    // Calculate real pipeline data
    const pipelineData: PipelineItem[] = useMemo(() => {
        const monthlyApplications = data?.recruitment_activity?.application_trends?.monthly || 0;
        const shortlisted = pipeline.talents_shortlisted || 0;
        const interviews = data?.recruitment_activity?.pre_chats?.scheduled || 0;
        const offers = data?.hiring_metrics?.offers_sent || 0;
        const hires = data?.recruitment_activity?.hires || 0;

        return [
            {
                key: '1',
                stage: 'Applications',
                count: monthlyApplications,
                target: calculateTarget(monthlyApplications, 'applications'),
                conversion: 100,
                avgDays: 1,
                status: calculateStatus(monthlyApplications, monthlyApplications * 0.9),
                description: 'New applications received'
            },
            {
                key: '2',
                stage: 'Screening',
                count: shortlisted,
                target: calculateTarget(monthlyApplications, 'screening'),
                conversion: monthlyApplications > 0 ? Math.round((shortlisted / monthlyApplications) * 100) : 0,
                avgDays: 3,
                status: calculateStatus(shortlisted, monthlyApplications * 0.3),
                description: 'Candidates shortlisted for review'
            },
            {
                key: '3',
                stage: 'Interviews',
                count: interviews,
                target: calculateTarget(shortlisted, 'interviews'),
                conversion: shortlisted > 0 ? Math.round((interviews / shortlisted) * 100) : 0,
                avgDays: 7,
                status: calculateStatus(interviews, shortlisted * 0.5),
                description: 'Interviews scheduled'
            },
            {
                key: '4',
                stage: 'Offers',
                count: offers,
                target: calculateTarget(interviews, 'offers'),
                conversion: interviews > 0 ? Math.round((offers / interviews) * 100) : 0,
                avgDays: 10,
                status: calculateStatus(offers, interviews * 0.3),
                description: 'Offers extended to candidates'
            },
            {
                key: '5',
                stage: 'Hires',
                count: hires,
                target: calculateTarget(offers, 'hires'),
                conversion: offers > 0 ? Math.round((hires / offers) * 100) : 0,
                avgDays: 14,
                status: calculateStatus(hires, offers * 0.8),
                description: 'Successful hires'
            },
        ];
    }, [pipeline, data]);

    // Calculate stage data for charts
    const stageData = useMemo(() => {
        return pipelineData.map(item => ({
            name: item.stage,
            candidates: item.count,
            conversion: item.conversion,
            target: item.target,
        }));
    }, [pipelineData]);

    // Calculate pipeline health status
    const statusData = useMemo(() => {
        const onTrack = pipelineData.filter(item => item.status === 'on-track').length;
        const atRisk = pipelineData.filter(item => item.status === 'at-risk').length;
        const delayed = pipelineData.filter(item => item.status === 'delayed').length;

        return [
            { name: 'On Track', value: onTrack, color: '#10B981' },
            { name: 'At Risk', value: atRisk, color: '#F59E0B' },
            { name: 'Delayed', value: delayed, color: '#EF4444' },
        ].filter(item => item.value > 0);
    }, [pipelineData]);

    // Optimized table columns
    const columns: ColumnsType<PipelineItem> = useMemo(() => [
        {
            title: 'Stage',
            dataIndex: 'stage',
            key: 'stage',
            fixed: 'left',
            width: 120,
            render: (text, record) => (
                <div>
                    <div className="font-medium">{text}</div>
                    <div className="text-xs text-gray-500">{record.description}</div>
                </div>
            ),
        },
        {
            title: 'Current',
            dataIndex: 'count',
            key: 'count',
            width: 100,
            sorter: (a, b) => a.count - b.count,
            render: (value) => <span className="font-bold text-lg">{value.toLocaleString()}</span>,
        },
        {
            title: 'Target',
            dataIndex: 'target',
            key: 'target',
            width: 100,
            render: (value) => <span className="text-gray-600">{value.toLocaleString()}</span>,
        },
        {
            title: 'Conversion',
            dataIndex: 'conversion',
            key: 'conversion',
            width: 120,
            render: (value) => (
                <div className="flex items-center">
                    <span className="font-semibold">{value}%</span>
                    {value > 30 ? (
                        <ArrowUpOutlined className="ml-1 text-green-500" />
                    ) : value < 15 ? (
                        <ArrowDownOutlined className="ml-1 text-red-500" />
                    ) : null}
                </div>
            ),
        },
        {
            title: 'Avg Days',
            dataIndex: 'avgDays',
            key: 'avgDays',
            width: 100,
            render: (value) => (
                <Tag color={value <= 7 ? 'green' : value <= 14 ? 'orange' : 'red'}>
                    {value} days
                </Tag>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            width: 120,
            render: (_, record) => {
                const color = record.status === 'on-track' ? 'green' :
                    record.status === 'at-risk' ? 'orange' : 'red';
                const text = record.status === 'on-track' ? 'On Track' :
                    record.status === 'at-risk' ? 'At Risk' : 'Delayed';
                return (
                    <Tag color={color} className="px-3 py-1 rounded-full font-medium">
                        {text}
                    </Tag>
                );
            },
        },
        {
            title: 'Progress',
            key: 'progress',
            width: 150,
            render: (_, record) => {
                const percent = (record.count / record.target) * 100;
                const displayPercent = Math.min(percent, 100);
                return (
                    <Tooltip title={`${Math.round(displayPercent)}% of target`}>
                        <Progress
                            percent={Math.round(displayPercent)}
                            size="small"
                            status={
                                displayPercent >= 100 ? 'success' :
                                    displayPercent >= 80 ? 'active' :
                                        'exception'
                            }
                            strokeColor={
                                displayPercent >= 100 ? '#10B981' :
                                    displayPercent >= 80 ? '#3B82F6' :
                                        '#EF4444'
                            }
                        />
                    </Tooltip>
                );
            },
        },
    ], []);

    const handleExportData = () => {
        try {
            const workbook = XLSX.utils.book_new();

            // Export pipeline data
            const pipelineExport = pipelineData.map(item => ({
                Stage: item.stage,
                Current: item.count,
                Target: item.target,
                'Conversion Rate': `${item.conversion}%`,
                'Average Days': item.avgDays,
                Status: item.status === 'on-track' ? 'On Track' : item.status === 'at-risk' ? 'At Risk' : 'Delayed',
                Progress: `${Math.round((item.count / item.target) * 100)}%`
            }));
            const ws1 = XLSX.utils.json_to_sheet(pipelineExport);
            XLSX.utils.book_append_sheet(workbook, ws1, "Pipeline Stages");

            XLSX.writeFile(workbook, `Pipeline_Status_${new Date().toISOString().split('T')[0]}.xlsx`);
            message.success('Pipeline data exported successfully');
        } catch (error) {
            message.error('Failed to export pipeline data');
        }
    };

    // Calculate pipeline efficiency
    const pipelineEfficiency = useMemo(() => {
        const totalInPipeline = pipeline.talents_in_pipeline || 0;
        const conversionRate = data?.quick_stats?.conversion_rate || 0;
        const avgTimeToFill = data?.hiring_metrics?.average_time_to_fill || 0;

        return {
            totalInPipeline,
            conversionRate,
            avgTimeToFill,
            efficiencyScore: Math.min(
                (conversionRate * 0.4) +
                (Math.max(0, 100 - (avgTimeToFill * 2)) * 0.3) +
                (pipeline.talents_shortlisted ? (pipeline.talents_shortlisted / totalInPipeline * 100 * 0.3) : 0),
                100
            )
        };
    }, [pipeline, data]);

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

    return (
        <Card
            title="Recruitment Pipeline Status"
            className="shadow-lg"
            extra={
                <Button
                    icon={<DownloadOutlined />}
                    onClick={handleExportData}
                    size="middle"
                >
                    Export Report
                </Button>
            }
        >
            <div className="space-y-6">
                {/* Pipeline Overview Cards */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <Card size="small" className="text-center h-full shadow-sm hover:shadow-md transition-shadow">
                            <div className="text-3xl font-bold text-blue-600 mb-2">
                                {pipeline.talents_in_pipeline?.toLocaleString() || 0}
                            </div>
                            <div className="text-sm text-gray-600 mb-3">Total in Pipeline</div>
                            <div className="flex justify-center gap-2">
                                <Tag color="green" className="px-3">
                                    Active: {pipeline.active_jobs || 0}
                                </Tag>
                                <Tag color="blue" className="px-3">
                                    Candidates: {pipeline.talents_in_pipeline || 0}
                                </Tag>
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} md={8}>
                        <Card size="small" className="text-center h-full shadow-sm hover:shadow-md transition-shadow">
                            <div className="text-3xl font-bold text-emerald-600 mb-2">
                                {pipeline.talents_shortlisted?.toLocaleString() || 0}
                            </div>
                            <div className="text-sm text-gray-600 mb-3">Shortlisted Candidates</div>
                            <Progress
                                percent={
                                    pipeline.talents_in_pipeline && pipeline.talents_shortlisted
                                        ? Math.round((pipeline.talents_shortlisted / pipeline.talents_in_pipeline) * 100)
                                        : 0
                                }
                                size="small"
                                status="active"
                                strokeColor="#10B981"
                            />
                            <div className="text-xs text-gray-500 mt-2">
                                {pipeline.talents_shortlisted || 0} of {pipeline.talents_in_pipeline || 0} candidates
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} md={8}>
                        <Card size="small" className="text-center h-full shadow-sm hover:shadow-md transition-shadow">
                            <div className="text-3xl font-bold text-violet-600 mb-2">
                                {pipeline.offers_pending?.toLocaleString() || 0}
                            </div>
                            <div className="text-sm text-gray-600 mb-3">Pending Offers</div>
                            <div className="mb-3">
                                <Tag color="orange" className="px-3">
                                    {pipeline.talents_shortlisted
                                        ? Math.round((pipeline.offers_pending / pipeline.talents_shortlisted) * 100)
                                        : 0
                                    }% of shortlisted
                                </Tag>
                            </div>
                            <div className="text-xs text-gray-500">
                                Offers sent: {data?.hiring_metrics?.offers_sent || 0}
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Charts */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                        <Card title="Pipeline Stages" className="h-full shadow-sm">
                            <div className="h-72">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={stageData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis
                                            dataKey="name"
                                            angle={-45}
                                            textAnchor="end"
                                            height={60}
                                            tick={{ fontSize: 12 }}
                                        />
                                        <YAxis yAxisId="left" />
                                        <YAxis yAxisId="right" orientation="right" />
                                        <ChartTooltip
                                            formatter={(value, name) => [
                                                name === 'candidates' ? (value !== undefined ? value.toLocaleString() : '0') : `${value !== undefined ? value : 0}%`,
                                                name === 'candidates' ? 'Candidates' : 'Conversion Rate'
                                            ]}
                                        />
                                        <Legend />
                                        <Bar
                                            yAxisId="left"
                                            dataKey="candidates"
                                            fill="#8884d8"
                                            name="Candidates"
                                            radius={[4, 4, 0, 0]}
                                        />
                                        <Bar
                                            yAxisId="right"
                                            dataKey="conversion"
                                            fill="#82ca9d"
                                            name="Conversion %"
                                            radius={[4, 4, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} lg={12}>
                        <Card
                            title="Pipeline Health"
                            className="h-full shadow-sm"
                            extra={
                                <Tag color={
                                    pipelineEfficiency.efficiencyScore >= 80 ? 'success' :
                                        pipelineEfficiency.efficiencyScore >= 60 ? 'warning' :
                                            'error'
                                }>
                                    Score: {Math.round(pipelineEfficiency.efficiencyScore)}%
                                </Tag>
                            }
                        >
                            <div className="h-72">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={statusData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={(entry) => `${entry.name}: ${entry.value}`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {statusData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <ChartTooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                                {statusData.map((item, index) => (
                                    <div key={index} className="p-2 bg-gray-50 rounded-lg">
                                        <div className="text-lg font-bold" style={{ color: item.color }}>
                                            {item.value}
                                        </div>
                                        <div className="text-sm text-gray-600">{item.name}</div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Optimized Pipeline Table */}
                <Card
                    title="Detailed Pipeline Status"
                    className="shadow-sm"
                    extra={
                        <Tooltip title="Shows real-time pipeline metrics">
                            <InfoCircleOutlined className="text-gray-400" />
                        </Tooltip>
                    }
                >
                    <Table
                        columns={columns}
                        dataSource={pipelineData}
                        pagination={false}
                        size="middle"
                        scroll={{ x: 800 }}
                        rowKey="key"
                        className="pipeline-table"
                    />
                </Card>

                {/* Key Metrics */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <Card size="small" className="h-full shadow-sm">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600 mb-2">
                                    {data?.hiring_metrics?.average_time_to_fill || 0} days
                                </div>
                                <div className="text-sm text-gray-600 mb-3">Avg Time to Fill</div>
                                <Progress
                                    percent={
                                        data?.hiring_metrics?.average_time_to_fill
                                            ? Math.max(0, 100 - (data.hiring_metrics.average_time_to_fill * 2))
                                            : 0
                                    }
                                    size="small"
                                    strokeColor="#3B82F6"
                                    className="mb-3"
                                />
                                <div className="text-xs text-gray-500">
                                    Industry avg: 42 days
                                </div>
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} md={8}>
                        <Card size="small" className="h-full shadow-sm">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-emerald-600 mb-2">
                                    {data?.recruitment_process?.average_job_vacancy_duration || 0} days
                                </div>
                                <div className="text-sm text-gray-600 mb-3">Avg Vacancy Duration</div>
                                <div className="mb-3">
                                    <Tag color="green" className="px-3">
                                        -{Math.max(0, 42 - (data?.recruitment_process?.average_job_vacancy_duration || 0))} days from target
                                    </Tag>
                                </div>
                                <div className="text-xs text-gray-500">
                                    Target: 30 days
                                </div>
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} md={8}>
                        <Card size="small" className="h-full shadow-sm">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-violet-600 mb-2">
                                    {data?.recruitment_activity?.job_posting_fill_rate || 0}%
                                </div>
                                <div className="text-sm text-gray-600 mb-3">Job Fill Rate</div>
                                <Progress
                                    percent={data?.recruitment_activity?.job_posting_fill_rate || 0}
                                    size="small"
                                    status={
                                        (data?.recruitment_activity?.job_posting_fill_rate || 0) > 70 ? 'success' :
                                            (data?.recruitment_activity?.job_posting_fill_rate || 0) > 50 ? 'normal' :
                                                'exception'
                                    }
                                    className="mb-3"
                                />
                                <div className="text-xs text-gray-500">
                                    Successfully filled positions
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

// Helper functions
function calculateTarget(current: number, stage: string): number {
    const multipliers: Record<string, number> = {
        applications: 1.2,
        screening: 0.35,
        interviews: 0.15,
        offers: 0.08,
        hires: 0.05
    };

    return Math.round(current / (multipliers[stage] || 1));
}

function calculateStatus(current: number, target: number): 'on-track' | 'at-risk' | 'delayed' {
    const percentage = (current / target) * 100;

    if (percentage >= 90) return 'on-track';
    if (percentage >= 70) return 'at-risk';
    return 'delayed';
}